import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FileText, ExternalLink } from "lucide-react";
import i18n from "../../../i18n";

export function getQuotationViewId(quotation) {
  const candidates = [
    quotation?.quotationId,
    quotation?.id,
    quotation?.raw?.id,
    quotation?.raw?.quotationId
  ];
  for (const value of candidates) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return String(parsed);
  }
  const fallback = quotation?.id || quotation?.quotationId;
  return fallback != null ? String(fallback).trim() : "";
}

export function getQuotationViewPath(quotation) {
  const id = getQuotationViewId(quotation);
  if (!id) return "";
  return `/sales/quotations/quotation/${encodeURIComponent(id)}`;
}

function formatAmount(amount, currency) {
  if (amount == null || amount === "") return "";
  const num = Number(amount);
  const formatted = Number.isFinite(num) ?
    num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) :
    String(amount);
  return currency ? `${currency} ${formatted}` : formatted;
}

export function FsmQuotationCard({ quotation, returnTo }) {
  const navigate = useNavigate();
  const t = i18n.t.bind(i18n);
  const viewPath = getQuotationViewPath(quotation);
  const amountLabel = formatAmount(
    quotation?.finalSellingPrice ?? quotation?.totalAmount,
    quotation?.currency
  );
  const title =
    quotation?.quotationNumber ||
    (quotation?.id ? `#${quotation.id}` : t("fsm.quotation"));

  const handleView = () => {
    if (!viewPath) return;
    navigate(viewPath, returnTo ? { state: { returnTo } } : undefined);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-(--color-text-border)/60 bg-workspace/25 px-3.5 py-3 hover:border-accent/35 hover:bg-workspace/45 transition">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <span className="h-9 w-9 shrink-0 grid place-items-center rounded-xl bg-primary/8 text-primary">
          <FileText className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {quotation?.status ?
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-sky-50 text-sky-700 border border-sky-100">
                {quotation.status}
              </span> :
              null}
            {amountLabel ?
              <span className="text-xs font-medium text-gray-500">{amountLabel}</span> :
              null}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={handleView}
        disabled={!viewPath}
        className="h-9 px-3.5 rounded-xl border border-primary text-primary bg-white font-semibold text-sm hover:bg-primary hover:text-white transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 shrink-0 w-full sm:w-auto">
        <Trans i18nKey="fsm.viewQuotation" />
        <ExternalLink className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
